import { useRef, useState } from 'react'
import { addWeeks, isoOf, mondayOf } from './format'
import { weekTouchesMonth } from './weeks'

/**
 * The Dotacion tab's viewport onto a project's real axis: a fixed window is
 * purely a slice of the same project -- the axis, the totals column, and the
 * budget figure all still cover the whole plan, only which week/month
 * columns get drawn changes. `isTrueFirstWeek`/`isTrueLastWeek` compare
 * against the FULL axis (`data.weeks`), never the visible slice, because the
 * edge add/remove controls act on the project's real edges and must only
 * ever appear on the column that truly is one, not on the window's own
 * visual boundary.
 */
export function useStaffingViewport(data) {
  const [jumpMonth, setJumpMonth] = useState('')
  const [viewStart, setViewStart] = useState('')     // '' = show the whole project
  const [viewWindow, setViewWindow] = useState(0)     // weeks; 0 = show the whole project
  const weekRefs = useRef(new Map())

  const allWeeks = data?.weeks || []
  const allMonths = data?.months || []
  const windowActive = viewWindow > 0 && !!viewStart
  const windowEnd = windowActive ? addWeeks(viewStart, viewWindow) : null
  const weeks = windowActive
    ? allWeeks.filter(w => w.monday >= viewStart && w.monday < windowEnd)
    : allWeeks
  const visibleMondays = new Set(weeks.map(w => w.monday))
  const months = windowActive
    ? allMonths.filter(m => weeks.some(w => weekTouchesMonth(w.monday, m.year, m.month)))
    : allMonths
  const visibleMonthKeys = new Set(months.map(m => `${m.year}-${m.month}`))
  const isTrueFirstWeek = (w) => w.monday === allWeeks[0]?.monday
  const isTrueLastWeek = (w) => w.monday === allWeeks[allWeeks.length - 1]?.monday

  const goWindow = (size) => {
    setViewWindow(size)
    if (!viewStart) setViewStart(allWeeks[0]?.monday || mondayOf(isoOf(new Date())))
  }
  const shiftWindow = (delta) => {
    if (!viewWindow) return
    setViewStart(v => addWeeks(v, delta * viewWindow))
  }
  const handleJumpTo = (value) => {
    setJumpMonth(value)
    if (!value) return
    const [year, month] = value.split('-').map(Number)
    const target = allWeeks.find(w => weekTouchesMonth(w.monday, year, month))
    if (!target) return
    // With a fixed-length window open, "jump" means move the window there;
    // otherwise it means scroll the full table to that column.
    if (viewWindow > 0) {
      setViewStart(target.monday)
    } else {
      const el = weekRefs.current.get(target.monday)
      if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
    }
  }

  return {
    jumpMonth, viewStart, setViewStart, viewWindow, setViewWindow, weekRefs,
    windowActive, weeks, months, visibleMondays, visibleMonthKeys,
    isTrueFirstWeek, isTrueLastWeek, goWindow, shiftWindow, handleJumpTo,
  }
}
